/**
 * v3.21-1706 — Adobe Acrobat Sign integration admin page (STOP POINT #1).
 * Renders connection status card.  Admin-only per backend gating.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw, Link2, Unlink } from 'lucide-react';

const STATE_COLOR = {
  connected:      'bg-emerald-100 text-emerald-800 border-emerald-200',
  unauthorized:   'bg-amber-100 text-amber-800 border-amber-200',
  not_connected:  'bg-slate-100 text-slate-700 border-slate-200',
  network_error:  'bg-red-100 text-red-800 border-red-200',
};

export default function AdobeSignIntegrationPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [search] = useSearchParams();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/integrations/adobe-sign/status');
      setStatus(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Adobe Sign status');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => {
    refresh();
    const err = search.get('error');
    const ok = search.get('status');
    if (err) toast.error(`Adobe Sign connect failed: ${err}`);
    if (ok === 'connected') toast.success('Adobe Sign connected');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get('/integrations/adobe-sign/oauth/connect');
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cannot start Adobe Sign OAuth');
      setConnecting(false);
    }
  };
  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post('/integrations/adobe-sign/verify', {});
      toast.success(`Verify: ${data.state}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verify failed');
    } finally { setVerifying(false); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect Adobe Acrobat Sign?')) return;
    try {
      await api.post('/integrations/adobe-sign/disconnect', {});
      toast.success('Disconnected');
      await refresh();
    } catch (e) { toast.error('Failed to disconnect'); }
  };

  const badge = (label, klass) =>
    <Badge className={`border ${klass || 'bg-slate-100 text-slate-700 border-slate-200'}`}
            data-testid="adobe-sign-status-badge">{label}</Badge>;

  return (
    <div className="p-4 sm:p-6 max-w-4xl min-w-0" data-testid="adobe-sign-integration-page">
      <div className="mb-4 flex items-start gap-3">
        <ShieldCheck className="h-6 w-6 text-indigo-600 mt-0.5" />
        <div>
          <h1 className="text-lg font-semibold text-slate-800">Adobe Acrobat Sign</h1>
          <p className="text-xs text-slate-500">PTC Secure Sign · OAuth-based signature transport for closing packets.</p>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-4">
          {loading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : !status ? (
            <div className="text-sm text-red-600">Unable to load status.</div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs text-slate-500">Connection Status</div>
                  <div className="mt-1">
                    {status.connected
                      ? badge('Connected', STATE_COLOR.connected)
                      : status.config_ready
                        ? badge('Not Connected', STATE_COLOR.not_connected)
                        : badge('Not Configured', STATE_COLOR.unauthorized)}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {!status.connected && (
                    <Button size="sm" onClick={connect} disabled={!status.config_ready || connecting}
                            data-testid="connect-adobe-sign-btn">
                      <Link2 className="h-3.5 w-3.5 mr-1" />
                      {connecting ? 'Redirecting…' : 'Connect Adobe Acrobat Sign'}
                    </Button>
                  )}
                  {status.connected && (
                    <>
                      <Button size="sm" variant="outline" onClick={verify} disabled={verifying}
                              data-testid="verify-adobe-sign-btn">
                        <RefreshCw className="h-3.5 w-3.5 mr-1" />
                        {verifying ? 'Verifying…' : 'Verify Connection'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={connect} disabled={connecting}
                              data-testid="reconnect-adobe-sign-btn">
                        <Link2 className="h-3.5 w-3.5 mr-1" /> Reconnect
                      </Button>
                      <Button size="sm" variant="outline" onClick={disconnect}
                              data-testid="disconnect-adobe-sign-btn"
                              className="text-red-600 border-red-200 hover:bg-red-50">
                        <Unlink className="h-3.5 w-3.5 mr-1" /> Disconnect
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 text-xs">
                <InfoRow label="Client ID" value={status.client_id_masked || '—'} testid="cfg-client-id" />
                <InfoRow label="Client Secret" value={status.client_secret_masked || '—'} testid="cfg-client-secret" />
                <InfoRow label="Adobe Shard" value={status.shard || '—'} testid="cfg-shard" />
                <InfoRow label="Authorize Host" value={status.authorize_host || '—'} testid="cfg-authorize-host" />
                <InfoRow label="Callback URI" value={status.redirect_uri} testid="cfg-redirect-uri" />
                <InfoRow label="Scopes" value={(status.scopes || []).join(' · ')} testid="cfg-scopes" />
                {status.connected && (
                  <>
                    <InfoRow label="Connected Account" value={status.connected_account_masked || '—'} testid="conn-account" />
                    <InfoRow label="Connected By" value={status.connected_by || '—'} testid="conn-by" />
                    <InfoRow label="Connected At" value={status.connected_at || '—'} testid="conn-at" />
                    <InfoRow label="Last Verified" value={
                      status.last_verified_at
                        ? `${status.last_verified_state} · ${status.last_verified_at.slice(0, 19).replace('T', ' ')}`
                        : '—'} testid="conn-last-verified" />
                    <InfoRow label="API Endpoint" value={status.api_access_point || '—'} testid="conn-api" />
                  </>
                )}
              </div>

              {!status.config_ready && (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2">
                  Adobe Sign is not yet configured for this environment. An
                  authorized admin must inject <code>ADOBE_SIGN_CLIENT_ID</code>
                  and <code>ADOBE_SIGN_CLIENT_SECRET</code> via the Deployment
                  Panel. The OAuth callback is locked to the registered
                  production callback (<code>hub.prestigiouscompanies.org</code>).
                </div>
              )}

              {status.connected && (
                <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 text-slate-700 text-xs px-3 py-2 space-y-1">
                  <div className="font-semibold text-slate-800">System-managed webhook</div>
                  <div>Adobe posts agreement lifecycle events directly to the Hub, so operators never have to configure a webhook manually.</div>
                  <div className="grid sm:grid-cols-2 gap-2 pt-1">
                    <InfoRow label="Webhook ID" value={status.webhook_id || 'Not yet created'} testid="webhook-id" />
                    <InfoRow label="Webhook Status" value={status.webhook_status || '—'} testid="webhook-status" />
                    <InfoRow label="Webhook URL" value={status.webhook_url || '—'} testid="webhook-url" />
                  </div>
                  <div className="pt-2">
                    <Button size="sm" variant="outline" data-testid="ensure-webhook-btn"
                            onClick={async () => {
                              try {
                                const { data } = await api.post('/integrations/adobe-sign/webhook/ensure', {});
                                if (data.ok) toast.success(data.created ? 'Webhook created' : 'Webhook already active');
                                else {
                                  const code = data.adobe_code ? ` · ${data.adobe_code}` : '';
                                  const msg = data.message ? ` — ${data.message}` : '';
                                  toast.error(`Webhook creation failed${code}${msg}`);
                                }
                                await refresh();
                              } catch (e) { toast.error(e?.response?.data?.detail || 'Webhook ensure failed'); }
                            }}>
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> Sync Webhook
                    </Button>
                  </div>
                </div>
              )}

              <div className="mt-3 rounded-md border border-sky-200 bg-sky-50 text-sky-800 text-xs px-3 py-2">
                <div className="font-semibold text-sky-900">LIVE-ONLY configuration</div>
                Adobe Acrobat Sign OAuth is executed against the production
                callback only. Preview environments can prepare configuration
                but the OAuth handshake completes on
                <code className="ml-1">https://hub.prestigiouscompanies.org/api/integrations/adobe-sign/oauth/callback</code>.
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function InfoRow({ label, value, testid }) {
  return (
    <div className="rounded border border-slate-200 p-2 bg-white min-w-0" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-xs text-slate-800 mt-0.5 break-all font-mono">{value}</div>
    </div>
  );
}
